import { useEffect, useRef, useState } from 'react';
import ImageGallery from 'react-image-gallery';
import { observer } from '../../../constants';
import styles from './ImageAttachments.module.scss';
import cx from 'classnames';
import { Icon, NoContent } from 'components';
import { useIntersectionObserver } from 'hooks';
import {
  selectCurrentPatientCase,
  selectMessageAttachments,
  selectMessageAttachmentsInfo,
  selectMessageAttachmentsReqParams,
  selectPatientActionIsLoading,
} from 'store/patients/selectors';
import { patientsActions } from 'store/patients/slice';
import { useDispatch, useSelector } from 'store/utils';

export const ImageAttachments = () => {
  const galleryRef = useRef<ImageGallery>(null);
  const galleryStartIndexRef = useRef(0);
  const ref = useRef<HTMLImageElement | null>(null);
  const [showGallery, setShowGallery] = useState(false);
  const dispatch = useDispatch();
  const params = useSelector(selectMessageAttachmentsReqParams);
  const attachments = useSelector(selectMessageAttachments) ?? [];
  const attachmentsInfo = useSelector(selectMessageAttachmentsInfo);
  const currentPatientCase = useSelector(selectCurrentPatientCase);
  const getMessageAttachments = useSelector(selectPatientActionIsLoading('getMessagesAttachments'));

  const patientCaseId = currentPatientCase?.id;

  useEffect(() => {
    if (!patientCaseId) return;
    dispatch(patientsActions.getMessagesAttachments({ patientCaseId, messageAttachmentParams: params }));
  }, [params]);

  const onIntersectionAction = () => {
    dispatch(
      patientsActions.setMessageAttachmentsRequestParams({
        ...params,
        page: params.page ? params.page + 1 : 1,
      }),
    );
  };

  useIntersectionObserver({
    info: attachmentsInfo,
    IntersectionObject: ref,
    IntersectionObserverProps: { rootMargin: observer.rootMargin },
    dependencies: [attachments],
    onIntersectionAction,
  });

  const galleryImages = attachments.reduce(
    (arr, file) => {
      const galleryImage = { original: file.file?.url, thumbnail: file.file?.url };
      arr.push(galleryImage);
      return arr;
    },
    [] as Array<{ original: string }>,
  );

  const handleImageClick = (index: number) => {
    setShowGallery(true);
    galleryStartIndexRef.current = index;
  };

  const handleGalleryImageClick = (event: any) => {
    if (event?.target?.currentSrc) {
      window.open(event.target.currentSrc);
    }
  };

  return (
    <div
      className={cx(styles.container, {
        [styles.noAttachments]: !attachments.length,
      })}>
      {attachments.length ? (
        <div className={styles.imageContainer}>
          {attachments.map((file, index) => (
            <img
              key={file.messageId}
              src={file.file?.url}
              alt={file.file?.name}
              onClick={() => handleImageClick(index)}
              ref={index === attachments.length - 1 ? ref : null}
            />
          ))}
        </div>
      ) : (
        <NoContent pageKey="discussionImages" className={styles.noContent} />
      )}
      {showGallery && (
        <div className={styles.galleryContainer}>
          <ImageGallery
            ref={galleryRef}
            items={galleryImages}
            additionalClass={styles.gallery}
            showNav={false}
            showPlayButton={false}
            showFullscreenButton={false}
            startIndex={galleryStartIndexRef.current}
            onClick={handleGalleryImageClick}
          />
          <Icon name="Close" className={styles.closeGalleryButton} onClick={() => setShowGallery(false)} />
        </div>
      )}
    </div>
  );
};
